import React, { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheckSquare, faSquare, faSearch, faExclamationTriangle, faCheckDouble, faSquareCheck } from '@fortawesome/free-solid-svg-icons';

/**
 * SpanSelector Component
 * 
 * Allows selection of elements (area data level) for repair projects
 * - No limit on element selection (configurable via maxSpans prop)
 * - Shows element details (area, corrosion, damage)
 * - Filters by direction, frame, corrosion category
 * - Visual feedback for selection status
 * - Bulk selection options (select all, select by filter)
 */
const SpanSelector = ({ bridgeId, onSelectionChange, maxSpans = null, levelLabel = 'Elements' }) => {
  const [availableSpans, setAvailableSpans] = useState([]);
  const [filteredSpans, setFilteredSpans] = useState([]);
  const [selectedSpanIds, setSelectedSpanIds] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  
  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(50);
  
  // Filters
  const [searchTerm, setSearchTerm] = useState('');
  const [directionFilter, setDirectionFilter] = useState('');
  const [corrosionFilter, setCorrosionFilter] = useState('');
  const [inspectionFilter, setInspectionFilter] = useState('');

  useEffect(() => {
    if (bridgeId) {
      fetchAvailableSpans();
    }
  }, [bridgeId]);

  useEffect(() => {
    applyFilters();
    setCurrentPage(1); // Reset to first page when filters change
  }, [searchTerm, directionFilter, corrosionFilter, inspectionFilter, availableSpans]);

  const fetchAvailableSpans = async () => {
    try {
      setLoading(true);
      console.log('Fetching spans for bridge:', bridgeId);
      console.log('API URL:', `${process.env.REACT_APP_API_URL || 'http://localhost:5005/api'}/repair-projects/available-spans?bridge_id=${bridgeId}`);
      
      const response = await fetch(
        `${process.env.REACT_APP_API_URL || 'http://localhost:5005/api'}/repair-projects/available-spans?bridge_id=${bridgeId}`,
        {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          }
        }
      );

      console.log('Response status:', response.status);
      const data = await response.json();
      console.log('Response data:', data);
      
      if (data.success) {
        console.log('Spans received:', data.data.length);
        setAvailableSpans(data.data);
        setFilteredSpans(data.data);
      } else {
        console.error('API error:', data.message);
        setError(data.message);
      }
    } catch (err) {
      console.error('Error fetching spans:', err);
      setError(`Failed to fetch available ${levelLabel.toLowerCase()}`);
    } finally {
      setLoading(false);
    }
  };

  const applyFilters = () => {
    let filtered = [...availableSpans];

    // Search filter
    if (searchTerm) {
      const search = searchTerm.toLowerCase();
      filtered = filtered.filter(span =>
        span.span_code?.toLowerCase().includes(search) ||
        span.span_name?.toLowerCase().includes(search) ||
        span.frame_name?.toLowerCase().includes(search) ||
        span.direction?.toLowerCase().includes(search) ||
        span.corrosion_category?.toLowerCase().includes(search) ||
        span.damage_severity?.toLowerCase().includes(search)
      );
    }

    // Direction filter
    if (directionFilter) {
      filtered = filtered.filter(span => span.direction === directionFilter);
    }

    // Corrosion filter
    if (corrosionFilter) {
      filtered = filtered.filter(span => span.corrosion_category === corrosionFilter);
    }

    // Inspection data filter
    if (inspectionFilter) {
      if (inspectionFilter === 'has_inspection') {
        filtered = filtered.filter(span => span.has_inspection_data === true);
      } else if (inspectionFilter === 'no_inspection') {
        filtered = filtered.filter(span => span.has_inspection_data === false);
      }
    }

    setFilteredSpans(filtered);
  };

  const handleSpanToggle = (spanId) => {
    let newSelection;
    
    if (selectedSpanIds.includes(spanId)) {
      // Deselect
      newSelection = selectedSpanIds.filter(id => id !== spanId);
    } else {
      // Select (no limit if maxSpans is null)
      if (maxSpans && selectedSpanIds.length >= maxSpans) {
        alert(`Maximum ${maxSpans} ${levelLabel.toLowerCase()} can be selected for a repair project`);
        return;
      }
      newSelection = [...selectedSpanIds, spanId];
    }

    setSelectedSpanIds(newSelection);
    
    // Notify parent component
    const selectedSpans = availableSpans.filter(span => newSelection.includes(span.id));
    onSelectionChange(newSelection, selectedSpans);
  };

  const getUniqueDirections = () => {
    return [...new Set(availableSpans.map(s => s.direction).filter(Boolean))];
  };

  const getUniqueCorrosionCategories = () => {
    return [...new Set(availableSpans.map(s => s.corrosion_category).filter(Boolean))];
  };

  // Pagination calculations
  const totalPages = Math.ceil(filteredSpans.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const currentSpans = filteredSpans.slice(startIndex, endIndex);

  const goToPage = (page) => {
    setCurrentPage(Math.max(1, Math.min(page, totalPages)));
  };

  const goToNextPage = () => {
    if (currentPage < totalPages) {
      setCurrentPage(currentPage + 1);
    }
  };

  const goToPrevPage = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
    }
  };

  const isSelected = (spanId) => selectedSpanIds.includes(spanId);
  const canSelect = !maxSpans || selectedSpanIds.length < maxSpans;

  // Bulk selection functions
  const selectAllSpans = () => {
    const allSpanIds = availableSpans.map(span => span.id);
    setSelectedSpanIds(allSpanIds);
    onSelectionChange(allSpanIds, availableSpans);
  };

  const selectFilteredSpans = () => {
    const filteredSpanIds = filteredSpans.map(span => span.id);
    const newSelection = [...new Set([...selectedSpanIds, ...filteredSpanIds])];
    setSelectedSpanIds(newSelection);
    const selectedSpans = availableSpans.filter(span => newSelection.includes(span.id));
    onSelectionChange(newSelection, selectedSpans);
  };

  const deselectAllSpans = () => {
    setSelectedSpanIds([]);
    onSelectionChange([], []);
  };

  const deselectFilteredSpans = () => {
    const filteredSpanIds = filteredSpans.map(span => span.id);
    const newSelection = selectedSpanIds.filter(id => !filteredSpanIds.includes(id));
    setSelectedSpanIds(newSelection);
    const selectedSpans = availableSpans.filter(span => newSelection.includes(span.id));
    onSelectionChange(newSelection, selectedSpans);
  };

  if (loading) {
    return (
      <div className="text-center py-8">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
        <p className="mt-4 text-gray-600">Loading available {levelLabel.toLowerCase()}...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-4">
        <p className="text-red-800">{error}</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Selection Status */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-lg font-semibold text-blue-900">
              {levelLabel} Selection
            </h3>
            <p className="text-sm text-blue-700 mt-1">
              {selectedSpanIds.length} {levelLabel.toLowerCase()}{selectedSpanIds.length !== 1 ? '' : ''} selected
              {maxSpans && ` (max ${maxSpans})`}
            </p>
          </div>
          {maxSpans && selectedSpanIds.length === maxSpans && (
            <div className="flex items-center text-yellow-700 bg-yellow-100 px-3 py-2 rounded">
              <FontAwesomeIcon icon={faExclamationTriangle} className="mr-2" />
              <span className="text-sm font-medium">Maximum reached</span>
            </div>
          )}
        </div>
      </div>

      {/* Filters */}
      <div className="space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {/* Search */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              <FontAwesomeIcon icon={faSearch} className="mr-2" />
              Search
            </label>
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder={`${levelLabel} code, name, frame...`}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
            <div className="mt-1 text-xs text-gray-500">
              Search by: {levelLabel.toLowerCase()} code, name, frame, direction, corrosion (C2/C3/C5), damage (A/B/C)
            </div>
          </div>

          {/* Direction Filter */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Direction</label>
            <select
              value={directionFilter}
              onChange={(e) => setDirectionFilter(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="">All Directions</option>
              {getUniqueDirections().map(dir => (
                <option key={dir} value={dir}>{dir}</option>
              ))}
            </select>
          </div>

          {/* Corrosion Filter */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Corrosion Category</label>
            <select
              value={corrosionFilter}
              onChange={(e) => setCorrosionFilter(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="">All Categories</option>
              {getUniqueCorrosionCategories().map(cat => (
                <option key={cat} value={cat}>{cat}</option>
              ))}
            </select>
          </div>

          {/* Inspection Data Filter */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Inspection Data</label>
            <select
              value={inspectionFilter}
              onChange={(e) => setInspectionFilter(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="">All {levelLabel}</option>
              <option value="has_inspection">Has Inspection Data</option>
              <option value="no_inspection">No Inspection Data</option>
            </select>
          </div>
        </div>

        {/* Filter Actions */}
        <div className="flex justify-between items-center">
          <div className="flex space-x-2">
            {/* Bulk Selection Buttons */}
            {availableSpans.length > 0 && (
              <>
                <button
                  onClick={selectAllSpans}
                  className="px-3 py-1 text-sm bg-green-100 text-green-700 rounded-md hover:bg-green-200 transition-colors"
                  title={`Select all ${levelLabel.toLowerCase()}`}
                >
                  <FontAwesomeIcon icon={faCheckDouble} className="mr-1" />
                  Select All
                </button>
                <button
                  onClick={deselectAllSpans}
                  className="px-3 py-1 text-sm bg-red-100 text-red-700 rounded-md hover:bg-red-200 transition-colors"
                  title={`Deselect all ${levelLabel.toLowerCase()}`}
                >
                  <FontAwesomeIcon icon={faSquare} className="mr-1" />
                  Deselect All
                </button>
                {filteredSpans.length > 0 && filteredSpans.length < availableSpans.length && (
                  <>
                    <button
                      onClick={selectFilteredSpans}
                      className="px-3 py-1 text-sm bg-blue-100 text-blue-700 rounded-md hover:bg-blue-200 transition-colors"
                      title={`Select all filtered ${levelLabel.toLowerCase()}`}
                    >
                      <FontAwesomeIcon icon={faSquareCheck} className="mr-1" />
                      Select Filtered
                    </button>
                    <button
                      onClick={deselectFilteredSpans}
                      className="px-3 py-1 text-sm bg-orange-100 text-orange-700 rounded-md hover:bg-orange-200 transition-colors"
                      title={`Deselect all filtered ${levelLabel.toLowerCase()}`}
                    >
                      <FontAwesomeIcon icon={faSquare} className="mr-1" />
                      Deselect Filtered
                    </button>
                  </>
                )}
              </>
            )}
          </div>
          
          {(searchTerm || directionFilter || corrosionFilter || inspectionFilter) && (
            <button
              onClick={() => {
                setSearchTerm('');
                setDirectionFilter('');
                setCorrosionFilter('');
                setInspectionFilter('');
              }}
              className="px-4 py-2 text-sm bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200 transition-colors"
            >
              Clear All Filters
            </button>
          )}
        </div>
      </div>

      {/* Spans List */}
      <div className="border border-gray-200 rounded-lg">
        <div className="bg-gray-50 px-4 py-3 border-b border-gray-200">
          <div className="flex justify-between items-center">
            <p className="text-sm text-gray-600">
              Showing {startIndex + 1}-{Math.min(endIndex, filteredSpans.length)} of {filteredSpans.length} {levelLabel.toLowerCase()}{filteredSpans.length !== 1 ? '' : ''}
              {availableSpans.length > 0 && (
                <span className="text-gray-500"> (from {availableSpans.length} total)</span>
              )}
            </p>
            {totalPages > 1 && (
              <div className="flex items-center space-x-2">
                <span className="text-sm text-gray-600">Page {currentPage} of {totalPages}</span>
              </div>
            )}
          </div>
        </div>
        
        <div className="max-h-96 overflow-y-auto">
          {availableSpans.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              <div className="mb-4">
                <FontAwesomeIcon icon={faExclamationTriangle} className="text-4xl text-gray-400" />
              </div>
              <p className="text-lg font-medium text-gray-700 mb-2">No {levelLabel.toLowerCase()} available</p>
              <p className="text-sm text-gray-500">
                This bridge may not have any {levelLabel.toLowerCase()} with valid area data, or {levelLabel.toLowerCase()} may not be active.
              </p>
            </div>
          ) : filteredSpans.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              <div className="mb-4">
                <FontAwesomeIcon icon={faSearch} className="text-4xl text-gray-400" />
              </div>
              <p className="text-lg font-medium text-gray-700 mb-2">No spans found matching your criteria</p>
              <p className="text-sm text-gray-500 mb-4">
                Try adjusting your search terms or filters to see more results.
              </p>
              <button
                onClick={() => {
                  setSearchTerm('');
                  setDirectionFilter('');
                  setCorrosionFilter('');
                  setInspectionFilter('');
                }}
                className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
              >
                Clear All Filters
              </button>
            </div>
          ) : (
            <>
              <div className="divide-y divide-gray-200">
                {currentSpans.map(span => (
                  <div
                    key={span.id}
                    onClick={() => (canSelect || isSelected(span.id)) && handleSpanToggle(span.id)}
                    className={`
                      p-3 cursor-pointer transition-colors
                      ${isSelected(span.id)
                        ? 'bg-blue-50 hover:bg-blue-100'
                        : canSelect
                          ? 'hover:bg-gray-50'
                          : 'opacity-50 cursor-not-allowed'
                      }
                    `}
                  >
                    <div className="flex items-start">
                      {/* Checkbox */}
                      <div className="flex-shrink-0 mt-1">
                        <FontAwesomeIcon
                          icon={isSelected(span.id) ? faCheckSquare : faSquare}
                          className={`text-lg ${isSelected(span.id) ? 'text-blue-600' : 'text-gray-400'}`}
                        />
                      </div>

                      {/* Span Details */}
                      <div className="ml-3 flex-1">
                        <div className="flex items-center justify-between">
                          <h4 className="text-sm font-semibold text-gray-900">
                            {span.span_code} - {span.span_name}
                          </h4>
                          {isSelected(span.id) && (
                            <span className="ml-2 px-2 py-1 bg-blue-600 text-white text-xs rounded">
                              Selected
                            </span>
                          )}
                        </div>

                        <div className="mt-2 grid grid-cols-2 md:grid-cols-4 gap-2 text-xs">
                          <div>
                            <span className="text-gray-500">Frame:</span>
                            <span className="ml-1 font-medium text-gray-900">{span.frame_name || 'N/A'}</span>
                          </div>
                          <div>
                            <span className="text-gray-500">Direction:</span>
                            <span className="ml-1 font-medium text-gray-900">{span.direction || 'N/A'}</span>
                          </div>
                          <div>
                            <span className="text-gray-500">Area:</span>
                            <span className="ml-1 font-medium text-gray-900">
                              {span.total_area ? `${parseFloat(span.total_area).toLocaleString()}m²` : 'N/A'}
                            </span>
                          </div>
                        </div>

                        <div className="mt-2 flex items-center space-x-4 text-xs">
                          {/* Damage Severity - Show latest inspection data if available */}
                          {(span.latest_damage_severity || span.damage_severity) && (
                            <div>
                              <span className="text-gray-500">Damage:</span>
                              <span 
                                className={`ml-1 px-2 py-0.5 rounded text-xs font-medium ${
                                  (span.latest_damage_severity || span.damage_severity) === 'C' ? 'bg-red-100 text-red-800' :
                                  (span.latest_damage_severity || span.damage_severity) === 'B' ? 'bg-yellow-100 text-yellow-800' :
                                  'bg-green-100 text-green-800'
                                }`}
                                onClick={(e) => e.stopPropagation()}
                              >
                                {span.latest_damage_severity || span.damage_severity}
                                {span.latest_damage_severity && <span className="ml-1 text-xs">(insp)</span>}
                              </span>
                            </div>
                          )}

                          {/* Corrosion Category - Show latest inspection data if available */}
                          {(span.latest_corrosion_category || span.corrosion_category) && (
                            <div>
                              <span className="text-gray-500">Corrosion:</span>
                              <span 
                                className={`ml-1 px-2 py-0.5 rounded text-xs font-medium ${
                                  (span.latest_corrosion_category || span.corrosion_category) === 'C5' ? 'bg-red-100 text-red-800' :
                                  (span.latest_corrosion_category || span.corrosion_category) === 'C3' ? 'bg-yellow-100 text-yellow-800' :
                                  'bg-green-100 text-green-800'
                                }`}
                                onClick={(e) => e.stopPropagation()}
                              >
                                {span.latest_corrosion_category || span.corrosion_category}
                                {span.latest_corrosion_category && <span className="ml-1 text-xs">(insp)</span>}
                              </span>
                            </div>
                          )}

                          {span.coating_system && (
                            <div>
                              <span className="text-gray-500">Coating:</span>
                              <span className="ml-1 font-medium text-gray-900">{span.coating_system}</span>
                            </div>
                          )}

                          {/* Inspection Data */}
                          <div>
                            <span className="text-gray-500">Inspection:</span>
                            {span.has_inspection_data ? (
                              <div className="ml-1 text-xs text-gray-600">
                                {span.latest_inspection_date && (
                                  <div>Latest: {new Date(span.latest_inspection_date).toLocaleDateString()}</div>
                                )}
                                {span.latest_damage_area && (
                                  <div>Area: {parseFloat(span.latest_damage_area).toFixed(1)}m²</div>
                                )}
                              </div>
                            ) : (
                              <span className="ml-1 text-xs text-gray-500">N/A</span>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
              
              {/* Pagination Controls */}
              {totalPages > 1 && (
                <div className="bg-gray-50 px-4 py-3 border-t border-gray-200">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-2">
                      <button
                        type="button"
                        onClick={goToPrevPage}
                        disabled={currentPage === 1}
                        className="px-3 py-1 text-sm bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        Previous
                      </button>
                      
                      {/* Smart pagination with ellipsis */}
                      <div className="flex space-x-1">
                        {/* Always show first page */}
                        <button
                          type="button"
                          onClick={() => goToPage(1)}
                          className={`px-3 py-1 text-sm rounded-md ${
                            currentPage === 1
                              ? 'bg-blue-600 text-white'
                              : 'bg-white border border-gray-300 text-gray-700 hover:bg-gray-50'
                          }`}
                        >
                          1
                        </button>
                        
                        {/* Show ellipsis if current page is far from start */}
                        {currentPage > 4 && (
                          <span className="px-2 py-1 text-sm text-gray-500">...</span>
                        )}
                        
                        {/* Show pages around current page */}
                        {Array.from({ length: totalPages }, (_, i) => {
                          const page = i + 1;
                          // Show pages that are within 2 of current page, but not first or last
                          if (page > 1 && page < totalPages && Math.abs(page - currentPage) <= 2) {
                            return (
                              <button
                                key={page}
                                type="button"
                                onClick={() => goToPage(page)}
                                className={`px-3 py-1 text-sm rounded-md ${
                                  currentPage === page
                                    ? 'bg-blue-600 text-white'
                                    : 'bg-white border border-gray-300 text-gray-700 hover:bg-gray-50'
                                }`}
                              >
                                {page}
                              </button>
                            );
                          }
                          return null;
                        })}
                        
                        {/* Show ellipsis if current page is far from end */}
                        {currentPage < totalPages - 3 && (
                          <span className="px-2 py-1 text-sm text-gray-500">...</span>
                        )}
                        
                        {/* Always show last page if more than 1 page */}
                        {totalPages > 1 && (
                          <button
                            type="button"
                            onClick={() => goToPage(totalPages)}
                            className={`px-3 py-1 text-sm rounded-md ${
                              currentPage === totalPages
                                ? 'bg-blue-600 text-white'
                                : 'bg-white border border-gray-300 text-gray-700 hover:bg-gray-50'
                            }`}
                          >
                            {totalPages}
                          </button>
                        )}
                      </div>
                      
                      <button
                        type="button"
                        onClick={goToNextPage}
                        disabled={currentPage === totalPages}
                        className="px-3 py-1 text-sm bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        Next
                      </button>
                    </div>
                    
                    <div className="text-sm text-gray-600">
                      Showing {startIndex + 1}-{Math.min(endIndex, filteredSpans.length)} of {filteredSpans.length}
                    </div>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {/* Selected Elements Summary */}
      {selectedSpanIds.length > 0 && (
        <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
          <h4 className="text-sm font-semibold text-gray-900 mb-2">Selected {levelLabel} Summary</h4>
          <div className="space-y-1">
            {availableSpans
              .filter(span => selectedSpanIds.includes(span.id))
              .map((span, index) => (
                <div key={span.id} className="text-sm text-gray-700">
                  {index + 1}. <span className="font-medium">{span.span_code}</span> 
                  {span.total_area && ` - ${parseFloat(span.total_area).toLocaleString()}m²`}
                </div>
              ))}
          </div>
          <div className="mt-3 pt-3 border-t border-gray-300">
            <div className="text-sm font-semibold text-gray-900">
              Total Area: {availableSpans
                .filter(span => selectedSpanIds.includes(span.id))
                .reduce((sum, span) => sum + (parseFloat(span.total_area) || 0), 0)
                .toLocaleString()}m²
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SpanSelector;

